// Merkan · Formatierung (Beträge in Cent, Zeit in UTC gespeichert, Anzeige Europe/Berlin)
import { t, gebiet, sprache } from './sprache';

const euroFmt = new Intl.NumberFormat(gebiet(), { style: 'currency', currency: 'EUR' });
const zahlFmt = new Intl.NumberFormat(gebiet(), { maximumFractionDigits: 3 });
const datumFmt = new Intl.DateTimeFormat(gebiet(), { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
const zeitFmt = new Intl.DateTimeFormat(gebiet(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const uhrFmt = new Intl.DateTimeFormat(gebiet(), { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });

/** Betrag aus Cent (bigint-Spalten neuer Tabellen) */
export function euroCent(cent: number | null | undefined): string {
  return euroFmt.format((cent ?? 0) / 100);
}

/** Betrag aus Euro (numeric-Spalten der Belege) */
export function euro(betrag: number | string | null | undefined): string {
  return euroFmt.format(Number(betrag ?? 0));
}

export function zahl(n: number | string | null | undefined): string {
  return zahlFmt.format(Number(n ?? 0));
}

export function datum(d: string | Date | null | undefined): string {
  if (!d) return '';
  const x = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T12:00:00Z') : new Date(d);
  return Number.isNaN(x.getTime()) ? '' : datumFmt.format(x);
}

export function zeit(d: string | Date | null | undefined): string {
  if (!d) return '';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '' : zeitFmt.format(x);
}

/** "vor 5 Minuten", "gestern 14:30", "12.10.2026" */
export function relativ(d: string | Date | null | undefined): string {
  if (!d) return '';
  const x = new Date(d);
  const s = (Date.now() - x.getTime()) / 1000;
  if (s < 60) return t('gerade eben');
  if (s < 3600) return t('vor {n} Min.', { n: Math.floor(s / 60) });
  const heute = datumFmt.format(new Date());
  const gestern = datumFmt.format(new Date(Date.now() - 86400_000));
  if (datumFmt.format(x) === heute) return t('heute {zeit}', { zeit: uhrFmt.format(x) });
  if (datumFmt.format(x) === gestern) return t('gestern {zeit}', { zeit: uhrFmt.format(x) });
  if (s < 6 * 86400) { const n = Math.max(2, Math.floor(s / 86400)); return t('vor {n} Tagen', { n }); }
  return datumFmt.format(x);
}

/** Tage bis Datum (negativ = überfällig) */
export function tageBis(d: string | null | undefined): number | null {
  if (!d) return null;
  const ziel = new Date(d.slice(0, 10) + 'T12:00:00Z').getTime();
  // Heutiges Datum in deutscher Zeit, nicht UTC (sonst zwischen 0 und 1 bzw. 2 Uhr um einen Tag verschoben)
  const heuteBerlin = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());
  const heute = new Date(heuteBerlin + 'T12:00:00Z').getTime();
  return Math.round((ziel - heute) / 86400_000);
}

/** Euro-Eingabe "1.234,56" oder "1234.56" in Cent */
export function centAusEingabe(s: string): number {
  const t = s.trim().replace(/\s|€/g, '');
  const normal = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  const n = Number(normal);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export function centAlsEingabe(cent: number | null | undefined): string {
  const x = ((cent ?? 0) / 100).toFixed(2);
  return sprache() === 'en' ? x : x.replace('.', ',');
}

export function initialen(text: string | null | undefined): string {
  const t = (text ?? '').trim();
  if (!t) return '?';
  const teile = t.split(/[\s@._-]+/).filter(Boolean);
  return ((teile[0]?.[0] ?? '') + (teile[1]?.[0] ?? '')).toUpperCase() || t[0].toUpperCase();
}

/** Platzhalter in Textbausteinen ersetzen: {vorgang}, {beleg}, {datum}, {benutzer}, ... */
export function platzhalter(text: string, werte: Record<string, string | null | undefined>): string {
  return text.replace(/\{(\w+)\}/g, (m, k) => (werte[k] != null ? String(werte[k]) : m));
}

export const SCHRITTE: { schluessel: string; name: string; phase: string }[] = [
  { schluessel: 'pruefen', name: t('Absender prüfen'), phase: 'anfrage' },
  { schluessel: 'erfassen', name: t('Artikel erfassen'), phase: 'anfrage' },
  { schluessel: 'lanfrage', name: t('Lieferantenanfrage'), phase: 'anfrage' },
  { schluessel: 'kalk', name: t('Angebote und Kalkulation'), phase: 'angebot' },
  { schluessel: 'angebot', name: t('Angebot an Kunden'), phase: 'angebot' },
  { schluessel: 'freigabe', name: t('Auftrag freigeben'), phase: 'auftrag' },
  { schluessel: 'vorkasse', name: t('Vorkasse abwarten'), phase: 'auftrag' },
  { schluessel: 'bestellt', name: t('Beschaffung'), phase: 'auftrag' },
  { schluessel: 'we', name: t('Wareneingang'), phase: 'lieferung' },
  { schluessel: 'versand', name: t('Versand an Kunden'), phase: 'lieferung' },
  { schluessel: 'zustellung', name: t('Zustellung'), phase: 'lieferung' },
  { schluessel: 'zahlung', name: t('Zahlung'), phase: 'abrechnung' },
  { schluessel: 'fertig', name: t('Abgeschlossen'), phase: 'abrechnung' },
];

export const PHASEN: { schluessel: string; name: string }[] = [
  { schluessel: 'anfrage', name: t('Anfrage') },
  { schluessel: 'angebot', name: t('Angebot') },
  { schluessel: 'auftrag', name: t('Auftrag') },
  { schluessel: 'lieferung', name: t('Lieferung') },
  { schluessel: 'abrechnung', name: t('Abrechnung') },
];

export const schrittName = (s: string) => SCHRITTE.find((x) => x.schluessel === s)?.name ?? s;

/** Ticketstatus im Service Hub (Spalten des Boards) */
export const NACHRICHT_STATUS: Record<string, string> = {
  neu: t('Neu'), in_bearbeitung: t('In Arbeit'), wartet_kunde: t('Wartet auf Antwort'), wartet_lieferant: t('Wartet auf Dritte'), erledigt: t('Gelöst'),
};
export const KATEGORIEN: Record<string, string> = {
  anfrage: t('Anfrage'), bestellung: t('Bestellung'), rechnung: t('Rechnung'), lieferantenangebot: t('Lieferantenangebot'),
  bereitstellung: t('Bereitstellungsmeldung'), werbung: t('Werbung'), sonstiges: t('Sonstiges'),
};
export const BELEGARTEN: Record<string, string> = {
  angebot: t('Angebot'), auftrag: t('Auftragsbestätigung'), lieferschein: t('Lieferschein'), rechnung: t('Rechnung'), gutschrift: t('Gutschrift'),
};
/** Technischer Belegstatus für Filter über alle Belegarten. In Listen und auf dem Beleg gilt belegStatus() je Belegart. */
export const BELEGSTATUS: Record<string, string> = {
  entwurf: t('Entwurf'), festgeschrieben: t('Ausgestellt oder gebucht'), bezahlt: t('Bezahlt'), storniert: t('Storniert'),
};

export type BelegStatusMarke = 'neutral' | 'info' | 'akzent' | 'gut' | 'warn' | 'fehler';
export interface BelegStatusAnzeige { name: string; erklaerung: string; marke: BelegStatusMarke }
/** Zusatzwissen für den fachlichen Status: Versand, Folgebelege, Zahlungen, Vorgangsstand */
export interface BelegStatusKontext {
  /** Manuell gesetzter Geschäftsstatus (Spalte belege.geschaeftsstatus); hat Vorrang vor dem abgeleiteten Stand */
  geschaeftsstatus?: string | null;
  versendet?: boolean; angenommen?: boolean; abgerechnet?: boolean; vorgangAbgeschlossen?: boolean;
  offen?: number | null; brutto?: number | null; faellig_am?: string | null;
}

const heuteIsoTag = () => new Date().toISOString().slice(0, 10);

/** Fachlicher Status je Belegart. Der gespeicherte Status bleibt entwurf, festgeschrieben, bezahlt oder storniert
 *  (GoBD-Sperre); angezeigt wird, was der Beleg im Geschäft bedeutet. */
export function belegStatus(art: string, status: string, k: BelegStatusKontext = {}): BelegStatusAnzeige {
  if (status === 'entwurf') return { name: t('Entwurf'), erklaerung: t('In Bearbeitung, noch ohne Nummer und jederzeit änderbar.'), marke: 'neutral' };
  if (status === 'storniert') {
    return art === 'angebot'
      ? { name: t('Zurückgezogen'), erklaerung: t('Das Angebot wurde zurückgezogen und gilt nicht mehr.'), marke: 'fehler' }
      : { name: t('Storniert'), erklaerung: t('Ungültig gemacht. Der Beleg bleibt zur Nachvollziehbarkeit unverändert erhalten.'), marke: 'fehler' };
  }
  const faelligVorbei = Boolean(k.faellig_am) && String(k.faellig_am).slice(0, 10) < heuteIsoTag();
  const manuell = k.geschaeftsstatus ? MANUELL[`${art}:${k.geschaeftsstatus}`] : undefined;
  if (manuell && !(art === 'rechnung' && status === 'bezahlt')) return manuell();
  switch (art) {
    case 'angebot':
      if (k.angenommen) return { name: t('Angenommen'), erklaerung: t('Der Kunde hat beauftragt, es gibt eine Auftragsbestätigung.'), marke: 'gut' };
      if (k.vorgangAbgeschlossen) return { name: t('Nicht beauftragt'), erklaerung: t('Der Vorgang ist abgeschlossen, ohne dass dieses Angebot beauftragt wurde.'), marke: 'neutral' };
      if (faelligVorbei) return { name: t('Abgelaufen'), erklaerung: t('Die Gültigkeit des Angebots ist überschritten.'), marke: 'warn' };
      if (k.versendet) return { name: t('Versendet'), erklaerung: t('An den Kunden gesendet, die Antwort steht aus.'), marke: 'info' };
      return { name: t('Ausgestellt'), erklaerung: t('Mit Nummer ausgestellt und nicht mehr änderbar, aber noch nicht versendet.'), marke: 'akzent' };
    case 'auftrag':
      if (k.abgerechnet) return { name: t('Erledigt'), erklaerung: t('Zu diesem Auftrag ist die Rechnung gestellt.'), marke: 'gut' };
      return { name: t('Bestätigt'), erklaerung: t('Auftrag bestätigt und nicht mehr änderbar. Die Rechnung steht noch aus.'), marke: 'info' };
    case 'lieferschein':
      if (k.vorgangAbgeschlossen) return MANUELL['lieferschein:ausgeliefert']();
      return { name: t('Ausgestellt'), erklaerung: t('Lieferschein ausgestellt, die Auslieferung ist noch nicht bestätigt.'), marke: 'info' };
    case 'gutschrift':
      if (status === 'bezahlt') return { name: t('Ausgeglichen'), erklaerung: t('Die Gutschrift ist ausgezahlt oder verrechnet.'), marke: 'gut' };
      return { name: t('Gebucht'), erklaerung: t('In der Buchhaltung gebucht und nach GoBD nicht mehr änderbar.'), marke: 'info' };
    default: {
      if (status === 'bezahlt') return { name: t('Bezahlt'), erklaerung: t('Die Rechnung ist vollständig bezahlt.'), marke: 'gut' };
      const offen = k.offen ?? null;
      const brutto = k.brutto ?? null;
      if (offen != null && brutto != null && offen > 0 && offen < brutto) {
        return faelligVorbei
          ? { name: t('Teilbezahlt, überfällig'), erklaerung: t('Ein Teil ist bezahlt, der Rest ist seit dem Fälligkeitsdatum offen.'), marke: 'fehler' }
          : { name: t('Teilbezahlt'), erklaerung: t('Ein Teil ist bezahlt, der Rest ist noch offen.'), marke: 'warn' };
      }
      if (faelligVorbei && (offen == null || offen > 0)) return { name: t('Überfällig'), erklaerung: t('Gebucht, aber nach dem Fälligkeitsdatum noch nicht bezahlt.'), marke: 'fehler' };
      return { name: t('Gebucht (offen)'), erklaerung: t('In der Buchhaltung gebucht und nach GoBD nicht mehr änderbar. Die Zahlung steht aus.'), marke: 'info' };
    }
  }
}

const MANUELL: Record<string, () => BelegStatusAnzeige> = {
  'angebot:versendet': () => ({ name: t('Versendet'), erklaerung: t('An den Kunden gesendet, die Antwort steht aus.'), marke: 'info' }),
  'angebot:angenommen': () => ({ name: t('Angenommen'), erklaerung: t('Der Kunde hat beauftragt, es gibt eine Auftragsbestätigung.'), marke: 'gut' }),
  'angebot:abgelehnt': () => ({ name: t('Abgelehnt'), erklaerung: t('Der Kunde hat das Angebot abgelehnt.'), marke: 'fehler' }),
  'angebot:abgelaufen': () => ({ name: t('Abgelaufen'), erklaerung: t('Die Gültigkeit des Angebots ist überschritten.'), marke: 'warn' }),
  'auftrag:erledigt': () => ({ name: t('Erledigt'), erklaerung: t('Zu diesem Auftrag ist die Rechnung gestellt.'), marke: 'gut' }),
  'lieferschein:ausgeliefert': () => ({ name: t('Ausgeliefert'), erklaerung: t('Die Ware ist mit diesem Lieferschein an den Kunden gegangen.'), marke: 'gut' }),
  'rechnung:gemahnt': () => ({ name: t('Gemahnt'), erklaerung: t('Zahlungserinnerung oder Mahnung ist versandt, die Zahlung steht aus.'), marke: 'fehler' }),
};

/** Manuell wählbare Status je Belegart (für „Status ändern“). 'automatisch' entfernt einen manuellen Status. */
export function belegStatusOptionen(art: string): { wert: string; name: string }[] {
  switch (art) {
    case 'angebot': return [
      { wert: 'versendet', name: t('Versendet') }, { wert: 'angenommen', name: t('Angenommen') }, { wert: 'abgelehnt', name: t('Abgelehnt') },
      { wert: 'abgelaufen', name: t('Abgelaufen') }, { wert: 'automatisch', name: t('Automatisch ermitteln') }];
    case 'auftrag': return [{ wert: 'erledigt', name: t('Erledigt') }, { wert: 'automatisch', name: t('Automatisch ermitteln') }];
    case 'lieferschein': return [{ wert: 'ausgeliefert', name: t('Ausgeliefert') }, { wert: 'automatisch', name: t('Automatisch ermitteln') }];
    case 'rechnung': return [{ wert: 'offen', name: t('Gebucht (offen)') }, { wert: 'gemahnt', name: t('Gemahnt') }, { wert: 'bezahlt', name: t('Bezahlt') }];
    case 'gutschrift': return [{ wert: 'offen', name: t('Gebucht') }, { wert: 'bezahlt', name: t('Ausgeglichen') }];
    default: return [];
  }
}

/** Alle Status, die für mindestens eine der Belegarten wählbar sind (Auswahl über mehrere Belege) */
export function belegStatusOptionenFuer(arten: string[]): { wert: string; name: string }[] {
  const aus = new Map<string, string>();
  for (const a of new Set(arten)) for (const o of belegStatusOptionen(a)) if (!aus.has(o.wert)) aus.set(o.wert, o.name);
  return [...aus].map(([wert, name]) => ({ wert, name }));
}

/** Abschluss eines Entwurfs je Belegart: Knopf, Rückfrage und Meldung */
export function belegAktion(art: string): { knopf: string; frage: string; fertig: string } {
  switch (art) {
    case 'angebot': return { knopf: t('Ausstellen'), frage: t('Angebot ausstellen?'), fertig: t('Angebot {nummer} ausgestellt.') };
    case 'auftrag': return { knopf: t('Auftrag bestätigen'), frage: t('Auftrag bestätigen?'), fertig: t('Auftragsbestätigung {nummer} erstellt.') };
    case 'lieferschein': return { knopf: t('Ausstellen'), frage: t('Lieferschein ausstellen?'), fertig: t('Lieferschein {nummer} ausgestellt.') };
    case 'gutschrift': return { knopf: t('Buchen'), frage: t('Gutschrift buchen?'), fertig: t('Gutschrift {nummer} gebucht.') };
    default: return { knopf: t('Buchen'), frage: t('Rechnung buchen?'), fertig: t('Rechnung {nummer} gebucht.') };
  }
}
export const STEUERARTEN: Record<string, string> = {
  inland: t('Inland'), eu: t('Innergemeinschaftlich'), drittland: t('Ausfuhr Drittland'), seeschifffahrt: t('Seeschifffahrt (§ 4 Nr. 2 UStG)'),
};
